import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';
import './DeviceInfoPopup.css';
import './DeviceInfoPopup.css';
import DeviceModal from '../common/DeviceModal';
import { sendCommand, fetchDeviceCommands } from '../../utils/commandService';
import { updateDeviceModel } from '../../utils/deviceService';
import { setDeviceStatus } from '../../services/api/traccarManagerApi';
import { sendMessage, getMessagingDevices } from '../../services/api/messageApi';
import { RED_FLAG_CANCEL_MESSAGE } from '../../config/redFlagConfig';

const DeviceInfoPopup = ({ device, onClose, deviceModels = [], onModelChange, onConfirmSos, onOpenStatus, isReadOnly = false }) => {
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [isModalActive, setIsModalActive] = useState(false);
  const [availableCommands, setAvailableCommands] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [messagingDevices, setMessagingDevices] = useState([]);

  useEffect(() => {
    if (!device) return;
    fetchDeviceCommands()
      .then(setAvailableCommands)
      .catch(err => console.error('fetchDeviceCommands error', err));

    // Fetch messaging devices to support SMS cancellation
    getMessagingDevices()
      .then(setMessagingDevices)
      .catch(err => console.error('getMessagingDevices error', err));
  }, [device]);

  useEffect(() => {
    if (device) setSelectedModel(device.deviceModel || device.model || '');
  }, [device]);

  const handleClearOk = async () => {
    const deviceId = device.deviceId || device.id;
    const attrs = device.attributes || {};
    const isRedFlag = attrs.CarStatus === 'REDFLAG' || attrs.CarStatus === 'REDFLAG_SEND' || attrs.CarStatus === 'REDFLAG_CONFIRM' || attrs.redflag === true;

    try {
      // If it's a Red Flag, attempt to send cancellation SMS first
      if (isRedFlag && messagingDevices.length > 0) {
        const traccarId = String(deviceId).trim();
        const msgDevice = messagingDevices.find(n =>
          String(n.DeviceIDTraccar || '').trim() === traccarId
        );

        if (msgDevice) {
          console.log(`Sending Red Flag cancellation SMS to ${traccarId}...`);
          try {
            await sendMessage(msgDevice, RED_FLAG_CANCEL_MESSAGE);
          } catch (smsErr) {
            console.error('Failed to send cancellation SMS', smsErr);
            // We proceed to clear attributes even if SMS fails, to allow UI cleanup
          }
        }
      }

      await setDeviceStatus(deviceId, 'CLEAR');
    } catch (err) {
      console.error('handleClearOk error', err);
    }
  };

  const handleModelChange = async (e) => {
    const newModel = e.target.value;
    setSelectedModel(newModel);
    try {
      await updateDeviceModel(device.deviceId || device.id, newModel);
      onModelChange && onModelChange(device.deviceId || device.id, newModel);
    } catch (err) {
      console.error('updateDeviceModel error', err);
    }
  };

  if (!device) return null;

  const handleSpeedButtonClick = () => {
    setSelectedDevice(device);
    setIsModalActive(true);
  };

  const isWarningStopped = (device.isSosActive === true) || (device.sos && device.isStopped);
  const statusText = device.isStale
    ? 'Disconnected'
    : isWarningStopped
      ? 'Warning'
      : (Number.isFinite(device.countdownSec)
        ? `stopped ${device.countdownSec}s…`
        : 'moving');
  const statusClass = device.isStale
    ? 'is-dark'
    : isWarningStopped ? 'is-warning' : 'is-success';

  const closeModal = () => {
    setIsModalActive(false);
    setSelectedDevice(null);
  };

  const rawDeviceName = (device.deviceName || '').trim();
  let teamNumber = rawDeviceName;
  let teamName = '';
  const hasHyphen = rawDeviceName.includes('-');

  if (hasHyphen) {
    const [numberPart, ...rest] = rawDeviceName.split('-');
    teamNumber = (numberPart || '').trim();
    teamName = rest.join('-').trim();
  }

  if (!teamNumber) {
    const fallbackId = device.deviceId || device.id;
    teamNumber = fallbackId ? String(fallbackId) : 'Unknown Device';
  }

  const hasTeamName = Boolean(teamName);
  const displayTeamNumber = teamNumber;
  const displaySpeed = Number.isFinite(device.speed)
    ? `${(device.speed * 1.852).toFixed(2)} km/h`
    : '0.00 km/h';
  const displayDistance = device.distanceOnPolyline !== 'Off-Track'
    ? `${device.distanceOnPolyline} km`
    : 'Off-Track';

  const headerLabel = hasHyphen ? 'Team' : 'Device';

  return (
    <>
      <div className="device-popup box is-size-7">
        <button className="delete is-small close-button" onClick={onClose}></button>

        <span className={`device-popup__status tag ${statusClass} is-light`}>{statusText}</span>

        <div className="device-popup__header">
          <span className="device-popup__label">{headerLabel}</span>
          <h3 className="device-popup__number">{displayTeamNumber}</h3>
          {hasTeamName && (
            <div className="device-popup__team">
              <span className="device-popup__team-name">{teamName}</span>
            </div>
          )}
        </div>

        {device.routeName && (
          <p className="device-popup__stage"><strong>Stage:</strong> {device.routeName}</p>
        )}

        <div className="device-popup__stats">
          <div className="device-popup__stat">
            <span className="device-popup__stat-label">Speed</span>
            <span className="device-popup__stat-value">{displaySpeed}</span>
          </div>
          <div className="device-popup__stat">
            <span className="device-popup__stat-label">Distance</span>
            <span className="device-popup__stat-value">{displayDistance}</span>
          </div>
        </div>

        <div className="device-popup__actions">
          {!isReadOnly && (
            <button
              className="button is-small is-fullwidth mb-2"
              onClick={() => onOpenStatus && onOpenStatus(device)}
              style={{ backgroundColor: '#f97316', color: 'white', fontWeight: 'bold', border: 'none' }}
            >
              CHECK STATUS
            </button>
          )}

          {/* CONFIRM SOS BUTTON */}
          {!isReadOnly && device.isSosActive && (
            <button
              className="button is-danger is-small is-fullwidth mb-2"
              onClick={() => onOpenStatus && onOpenStatus(device)}
              style={{ fontWeight: 'bold' }}
            >
              Close SOS Status
            </button>
          )}
          {/* CLEAR RED FLAG BUTTON — hidden */}
          {/* CLEAR OK BUTTON */}
          {!isReadOnly && device.attributes?.CarStatus === 'OK' && (
            <button
              className="button is-success is-small is-fullwidth mb-2"
              onClick={handleClearOk}
              style={{ fontWeight: 'bold' }}
            >
              Clear OK Status
            </button>
          )}
          {!isReadOnly && (
            <button
              type="button"
              className="button is-info is-light is-small device-popup__history"
              onClick={handleSpeedButtonClick}
            >
              <span className="icon is-small">
                <FontAwesomeIcon icon={faChartLine} />
              </span>
              <span>Speed history</span>
            </button>
          )}
          {!isReadOnly && availableCommands.map(cmd => (
            <button
              key={cmd.id}
              className="button is-link is-small"
              onClick={() => sendCommand(device.id, cmd)}
            >
              {cmd.description || cmd.type}
            </button>
          ))}
        </div>

        {/* More options / model selector — visible for admin only */}
        {!isReadOnly && deviceModels.length > 0 && (
          <div className="device-popup__options">
            <button
              className="button is-text is-small device-popup__options-trigger"
              onClick={() => setShowOptions(!showOptions)}
            >
              {showOptions ? 'Hide options' : 'More options'}
            </button>
            {showOptions && (
              <div className="device-popup__model-select">
                <label className="label is-small">Model:</label>
                <div className="control">
                  <div className="select is-small is-fullwidth">
                    <select value={selectedModel} onChange={handleModelChange}>
                      {deviceModels.map(m => (
                        <option key={m || 'default'} value={m}>{m || 'Default'}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {isModalActive && (
        <DeviceModal isActive={isModalActive} device={selectedDevice} onClose={closeModal} />
      )}
    </>
  );
};

export default DeviceInfoPopup;
